import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
    collection,
    addDoc,
    Timestamp,
    query,
    orderBy,
    onSnapshot,
    getDoc,
    deleteDoc,
    doc,
    updateDoc,
    where,
    getCountFromServer
} from "firebase/firestore";
import { auth, db } from "../firebase";

import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TextField,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Pagination
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

export default function Products() {
    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        timer: 3000,
        showConfirmButton: false
    });

    const [propertyId, setPropertyId] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [products, setProducts] = useState([]);

    const [searchText, setSearchText] = useState("");

    const [departmentid, setDepartmentid] = useState("");
    const [product, setProduct] = useState("");

    const [editId, setEditId] = useState("");
    const [editDepartmentid, setEditDepartmentid] = useState("");
    const [editProduct, setEditProduct] = useState("");

    const [openAdd, setOpenAdd] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    /* ---------------- FETCH PROPERTY ---------------- */

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            if (user) {
                const docRef = doc(db, "authroles", user.uid);
                const snap = await getDoc(docRef);
                if (snap.exists()) {
                    const propId = snap.data().propertyId;
                    setPropertyId(propId);
                    fetchDepartments(propId);
                    fetchProducts(propId);
                }
            }
        });

        return () => unsubscribe();
    }, []);

    /* ---------------- FETCH DEPARTMENTS ---------------- */

    const fetchDepartments = (propId) => {
        const q = query(
            collection(db, "departments"),
            where("propertyId", "==", propId),
        );

        onSnapshot(q, (snapshot) => {
            setDepartments(
                snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data()
                }))
            );
        });
    };

    /* ---------------- FETCH PRODUCTS ---------------- */

    const fetchProducts = (propId) => {
        const q = query(
            collection(db, "storeproducts"),
            where("propertyId", "==", propId),
            orderBy("product", "asc")
        );

        onSnapshot(q, (snapshot) => {
            const productData = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data()
            }));

            setProducts(productData); // ✅ only raw data
        });
    };

    useEffect(() => {
        if (departments.length === 0 || products.length === 0) return;

        const updatedProducts = products.map((item) => {
            const dep = departments.find((d) => d.id === item.departmentid);

            return {
                ...item,
                departmentName: dep ? dep.department : ""
            };
        });

        setProducts(updatedProducts);

    }, [departments]);
    /* ---------------- ADD ---------------- */

    const handleAdd = async () => {
        if (!product || !departmentid) {
            Toast.fire({ icon: "error", title: "Fill all fields" });
            return;
        }

        await addDoc(collection(db, "storeproducts"), {
            departmentid,
            product,
            propertyId,
            createdAt: Timestamp.now()
        });

        await updateDepartmentCount(departmentid);

        setProduct("");
        setOpenAdd(false);

        Toast.fire({ icon: "success", title: "Product Added" });
    };

    /* ---------------- DELETE ---------------- */

    const handleDelete = async (id, depId) => {
        if (!window.confirm("Are you sure?")) return;

        await deleteDoc(doc(db, "storeproducts", id));
        await updateDepartmentCount(depId);

        Toast.fire({ icon: "success", title: "Deleted Successfully" });
    };

    /* ---------------- UPDATE ---------------- */

    const handleUpdate = async () => {
        if (!editProduct || !editDepartmentid) {
            Toast.fire({ icon: "error", title: "Fill all fields" });
            return;
        }

        await updateDoc(doc(db, "storeproducts", editId), {
            departmentid: editDepartmentid,
            product: editProduct
        });

        await updateDepartmentCount(editDepartmentid);

        setOpenEdit(false);
        Toast.fire({ icon: "success", title: "Updated Successfully" });
    };

    /* ---------------- UPDATE DEPARTMENT COUNT ---------------- */

    const updateDepartmentCount = async (depId) => {
        const q = query(
            collection(db, "storeproducts"),
            where("departmentid", "==", depId)
        );

        const snapshot = await getCountFromServer(q);

        await updateDoc(doc(db, "departments", depId), {
            noofrooms: snapshot.data().count
        });
    };

    /* ---------------- SEARCH ---------------- */

    const filtered = products.filter((item) =>
        item.product.toLowerCase().includes(searchText.toLowerCase())
    );

    /* ---------------- PAGINATION ---------------- */

    const indexLast = currentPage * itemsPerPage;
    const indexFirst = indexLast - itemsPerPage;
    const currentItems = filtered.slice(indexFirst, indexLast);
    const totalPages = Math.ceil(filtered.length / itemsPerPage);

    /* ---------------- UI ---------------- */

    return (
        <Box className="content-wrapper" sx={{ p: 4, backgroundColor: "#f4f6f9", minHeight: "100vh" }}>
            <Typography variant="h5" fontWeight="bold" mb={3}>
                Store Products
            </Typography>

            <Card sx={{ borderRadius: 3 }}>
                <CardContent>

                    <Box display="flex" justifyContent="space-between" mb={2}>
                        <TextField
                            label="Search Product"
                            size="small"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />

                        <Button variant="contained" onClick={() => setOpenAdd(true)}>
                            Add Product
                        </Button>
                    </Box>

                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Sr No</TableCell>
                                <TableCell>Department</TableCell>
                                <TableCell>Product</TableCell>
                                <TableCell align="center">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {currentItems.map((item, index) => (
                                <TableRow key={item.id}>
                                    <TableCell>{indexFirst + index + 1}</TableCell>
                                    <TableCell>{item.departmentName}</TableCell>
                                    <TableCell>{item.product}</TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            color="primary"
                                            onClick={() => {
                                                setEditId(item.id);
                                                setEditDepartmentid(item.departmentid);
                                                setEditProduct(item.product);
                                                setOpenEdit(true);
                                            }}
                                        >
                                            <EditIcon />
                                        </IconButton>

                                        <IconButton
                                            color="error"
                                            onClick={() =>
                                                handleDelete(item.id, item.departmentid)
                                            }
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <Box display="flex" justifyContent="center" mt={3}>
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={(e, value) => setCurrentPage(value)}
                            color="primary"
                        />
                    </Box>
                </CardContent>
            </Card>

            {/* ADD DIALOG */}
            <Dialog open={openAdd} onClose={() => setOpenAdd(false)}>
                <DialogTitle>Add Product</DialogTitle>
                <DialogContent>
                    <Select
                        fullWidth
                        value={departmentid}
                        onChange={(e) => setDepartmentid(e.target.value)}
                        sx={{ mt: 2 }}
                    >
                        {departments.map((dep) => (
                            <MenuItem key={dep.id} value={dep.id}>
                                {dep.department}
                            </MenuItem>
                        ))}
                    </Select>

                    <TextField
                        fullWidth
                        label="Product Name"
                        value={product}
                        onChange={(e) => setProduct(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAdd}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* EDIT DIALOG */}
            <Dialog open={openEdit} onClose={() => setOpenEdit(false)}>
                <DialogTitle>Edit Product</DialogTitle>
                <DialogContent>
                    <Select
                        fullWidth
                        value={editDepartmentid}
                        onChange={(e) => setEditDepartmentid(e.target.value)}
                        sx={{ mt: 2 }}
                    >
                        {departments.map((dep) => (
                            <MenuItem key={dep.id} value={dep.id}>
                                {dep.department}
                            </MenuItem>
                        ))}
                    </Select>

                    <TextField
                        fullWidth
                        label="Product Name"
                        value={editProduct}
                        onChange={(e) => setEditProduct(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleUpdate}>
                        Update
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}